export interface ContentSection {
  type: 'paragraph' | 'heading' | 'list' | 'quote' | 'image'
  content?: string
  items?: string[]
  src?: string
  alt?: string
  caption?: string
}

export interface NewsArticle {
  slug: string
  title: string
  date: string // ISO format
  category:
    | 'Company News'
    | 'Procurement'
    | 'Manufacturing'
    | 'Product Updates'
    | 'Insights'
  excerpt: string
  heroImage: string
  body: ContentSection[]
}

export function formatDate(isoDate: string): string {
  const date = new Date(isoDate)
  const day = date.getDate()
  const month = date.toLocaleDateString('en-GB', { month: 'long' })
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

export const newsArticles: NewsArticle[] = [
  {
    slug: 'chiltern-global-secures-bluelight-framework-renewal',
    title: 'Chiltern Global Secures BlueLight Commercial Framework Renewal',
    date: '2026-01-15',
    category: 'Procurement',
    excerpt:
      'We are pleased to announce our continued appointment to the BlueLight Commercial framework, enabling streamlined procurement for emergency services across the United Kingdom.',
    heroImage:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Chiltern Global has been reappointed to the BlueLight Commercial framework for a further four-year term, reinforcing our position as a trusted supplier to police forces, fire and rescue services, and ambulance trusts across the United Kingdom.',
      },
      {
        type: 'heading',
        content: 'What this means for buyers',
      },
      {
        type: 'paragraph',
        content:
          'The framework appointment provides public sector buyers with a compliant, pre-tendered route to market for protective clothing, operational equipment, and specialist textiles. Procurement teams can access our full product range with the assurance that due diligence, price benchmarking, and compliance checks have already been conducted.',
      },
      {
        type: 'list',
        items: [
          'Direct award and mini-competition options available',
          'Pre-agreed terms and conditions',
          'Benchmarked pricing structures',
          'Full audit trail for procurement activities',
        ],
      },
      {
        type: 'quote',
        content:
          'This renewal reflects our ongoing commitment to supporting emergency services with high-quality, specification-grade equipment through efficient procurement routes.',
      },
      {
        type: 'paragraph',
        content:
          'For more information on procuring through the BlueLight Commercial framework, please contact our frameworks team or visit our Frameworks page.',
      },
    ],
  },
  {
    slug: 'new-manufacturing-facility-expansion',
    title: 'Chiltern Global Announces Manufacturing Facility Expansion',
    date: '2025-12-08',
    category: 'Company News',
    excerpt:
      'Investment in expanded production capacity will enable faster turnaround times and increased volume capability for defence and security contracts.',
    heroImage:
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Chiltern Global is investing in a significant expansion of our manufacturing and logistics capabilities. The expansion will increase our production capacity by approximately 40 per cent, enabling us to better serve growing demand from defence, police, and medical sector clients.',
      },
      {
        type: 'heading',
        content: 'Enhanced capabilities',
      },
      {
        type: 'paragraph',
        content:
          'The expanded facility will house additional cutting and sewing lines, dedicated quality control stations, and increased warehousing space. This investment supports our commitment to maintaining short lead times while scaling production for larger programme requirements.',
      },
      {
        type: 'list',
        items: [
          'Additional production lines for tactical equipment',
          'Expanded quality assurance laboratory',
          'Increased raw material storage capacity',
          'New dispatch and logistics area',
        ],
      },
      {
        type: 'paragraph',
        content:
          'The expansion is scheduled for completion in the second quarter of 2026. Existing operations will continue uninterrupted throughout the construction period.',
      },
    ],
  },
  {
    slug: 'iso-14001-environmental-certification-achieved',
    title: 'Chiltern Global Achieves ISO 14001 Environmental Certification',
    date: '2025-11-22',
    category: 'Company News',
    excerpt:
      'Our commitment to environmental responsibility has been recognised with ISO 14001:2015 certification for environmental management systems.',
    heroImage:
      'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Chiltern Global has achieved ISO 14001:2015 certification, demonstrating our commitment to minimising environmental impact across all manufacturing and operational activities. This certification joins our existing ISO 9001:2015 quality management accreditation.',
      },
      {
        type: 'heading',
        content: 'Environmental commitments',
      },
      {
        type: 'paragraph',
        content:
          'The certification process involved a comprehensive review of our environmental management practices, including waste reduction initiatives, energy efficiency measures, and sustainable sourcing policies.',
      },
      {
        type: 'list',
        items: [
          'Waste reduction and recycling programmes',
          'Energy efficiency improvements across facilities',
          'Sustainable material sourcing policies',
          'Regular environmental impact assessments',
        ],
      },
      {
        type: 'quote',
        content:
          'Environmental responsibility is increasingly important to our public sector clients. This certification provides assurance that our operations meet internationally recognised environmental standards.',
      },
      {
        type: 'paragraph',
        content:
          'The certification is valid for three years, subject to annual surveillance audits. Full details of our certifications are available on our Certifications page.',
      },
    ],
  },
  {
    slug: 'new-modular-plate-carrier-system-launched',
    title: 'New Modular Plate Carrier System Launched for Defence Market',
    date: '2025-10-30',
    category: 'Product Updates',
    excerpt:
      'Our latest plate carrier system offers enhanced modularity and reduced weight for military and specialist police applications.',
    heroImage:
      'https://images.unsplash.com/photo-1595590424283-b8f17842773f?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Chiltern Global has launched a new modular plate carrier system designed for military and specialist police applications. The system features a lightweight skeletal design with comprehensive MOLLE coverage for mission-specific configuration.',
      },
      {
        type: 'heading',
        content: 'Key features',
      },
      {
        type: 'list',
        items: [
          'Accepts SAPI and ESAPI ballistic plates',
          'Laser-cut MOLLE attachment points',
          'Quick-release buckle system',
          'Adjustable cummerbund with side plate capability',
          'Available in Multicam, MTP, and solid colours',
        ],
      },
      {
        type: 'paragraph',
        content:
          'The carrier has been developed in consultation with end users to ensure it meets the practical demands of operational environments. Weight has been reduced by 15 per cent compared to previous generation systems while maintaining durability and load-bearing capacity.',
      },
      {
        type: 'heading',
        content: 'Availability',
      },
      {
        type: 'paragraph',
        content:
          'The new plate carrier system is available for evaluation and procurement through our standard channels. Technical data sheets and sizing guides are available on request.',
      },
    ],
  },
  {
    slug: 'supply-chain-resilience-defence-procurement',
    title: 'Building Supply Chain Resilience in Defence Procurement',
    date: '2025-09-18',
    category: 'Insights',
    excerpt:
      'How Chiltern Global is strengthening logistics networks to ensure continuity of supply for critical operational equipment.',
    heroImage:
      'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Supply chain resilience has become a critical consideration for defence and security procurement. Recent global events have highlighted the importance of robust, diversified supply networks that can maintain delivery schedules even during periods of disruption.',
      },
      {
        type: 'heading',
        content: 'Our approach to resilience',
      },
      {
        type: 'paragraph',
        content:
          'Chiltern Global has invested significantly in supply chain diversification and risk management. We maintain relationships with multiple qualified suppliers for critical materials and components, reducing dependency on any single source.',
      },
      {
        type: 'list',
        items: [
          'Multi-source supplier relationships for key materials',
          'Strategic stockholding of critical components',
          'Regular supplier audits and performance reviews',
          'Contingency planning for supply disruptions',
        ],
      },
      {
        type: 'quote',
        content:
          'Our clients depend on us to deliver when it matters. Supply chain resilience is not optional; it is fundamental to our service commitment.',
      },
      {
        type: 'paragraph',
        content:
          'We continue to monitor global supply conditions and adjust our procurement strategies accordingly. Clients with specific supply chain concerns are encouraged to discuss requirements with our team.',
      },
    ],
  },
  {
    slug: 'ypo-framework-appointment-announced',
    title: 'Chiltern Global Appointed to YPO Framework',
    date: '2025-08-25',
    category: 'Procurement',
    excerpt:
      'Our appointment to the YPO framework expands procurement options for local government, education, and public sector organisations.',
    heroImage:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Chiltern Global has been appointed to the YPO framework, providing an additional compliant procurement route for public sector organisations. The framework covers emergency response equipment, facilities management supplies, and specialist clothing.',
      },
      {
        type: 'heading',
        content: 'Framework coverage',
      },
      {
        type: 'paragraph',
        content:
          'The YPO framework is accessible to a wide range of public sector bodies, including local government, education institutions, emergency services, and NHS organisations. It provides a pre-tendered route to market with standardised terms and competitive pricing.',
      },
      {
        type: 'list',
        items: [
          'Local government and councils',
          'Schools, colleges, and universities',
          'Emergency services',
          'NHS trusts and healthcare providers',
          'Housing associations',
        ],
      },
      {
        type: 'paragraph',
        content:
          'For guidance on procuring through the YPO framework, please contact our frameworks team or submit a framework pack request through our website.',
      },
    ],
  },
  {
    slug: 'flame-retardant-textiles-prison-sector',
    title: 'Flame Retardant Textile Solutions for the Prison Sector',
    date: '2025-07-12',
    category: 'Manufacturing',
    excerpt:
      'Specialist flame retardant fabrics and finished products designed to meet the specific requirements of custodial environments.',
    heroImage:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Fire safety in custodial environments presents unique challenges. Chiltern Global manufactures a range of flame retardant textile products specifically designed for prison and secure facility applications, meeting relevant British Standards and Ministry of Justice specifications.',
      },
      {
        type: 'heading',
        content: 'Product range',
      },
      {
        type: 'paragraph',
        content:
          'Our flame retardant range includes bedding, curtains, mattress covers, and soft furnishings manufactured from inherently flame retardant or treated fabrics. All products are designed to withstand the demands of institutional laundering while maintaining fire safety performance.',
      },
      {
        type: 'list',
        items: [
          'Inherently flame retardant bedding sets',
          'FR curtains and window coverings',
          'Mattress encasements and protectors',
          'Soft furnishing covers and upholstery',
        ],
      },
      {
        type: 'heading',
        content: 'Compliance and testing',
      },
      {
        type: 'paragraph',
        content:
          'All flame retardant products are tested to relevant standards including BS 5867 and BS 7175. Certificates of conformity and test reports are available for procurement review.',
      },
    ],
  },
  {
    slug: 'cyber-essentials-plus-certification-renewed',
    title: 'Cyber Essentials Plus Certification Renewed',
    date: '2025-06-05',
    category: 'Company News',
    excerpt:
      'Chiltern Global has renewed its Cyber Essentials Plus certification, demonstrating continued commitment to information security.',
    heroImage:
      'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1200&q=80',
    body: [
      {
        type: 'paragraph',
        content:
          'Chiltern Global has successfully renewed its Cyber Essentials Plus certification following an independent technical audit. The certification demonstrates our commitment to protecting client data and maintaining robust information security controls.',
      },
      {
        type: 'heading',
        content: 'Why this matters',
      },
      {
        type: 'paragraph',
        content:
          'Cyber Essentials Plus is the UK government-backed cyber security standard. It requires organisations to demonstrate effective controls against common cyber threats through independent technical verification. For defence and public sector clients, this certification provides assurance that sensitive information is handled securely.',
      },
      {
        type: 'list',
        items: [
          'Boundary firewalls and internet gateways',
          'Secure configuration of systems',
          'Access control and user management',
          'Malware protection measures',
          'Patch management procedures',
        ],
      },
      {
        type: 'paragraph',
        content:
          'The certification is valid for 12 months and will be renewed annually. Details of all our certifications are available on the Certifications page.',
      },
    ],
  },
]
